<template>
    <div>
        <sui-message class="info">
            <p>
                <sui-icon color="blue" name="info circle triangle"/>
                {{ $t('bindings.explication') }}
            </p>
        </sui-message>

        <div is="sui-header">
            <sui-form @submit="copyBindings" class="form-header">
                <sui-button floated="left" @click="displayCode" :content="$t('bindings.displayCode')"/>
                <sui-form-fields inline>
                    <label>{{ $t('bindings.form.copyFrom') }}</label>
                    <sui-form-field>
                        <sui-dropdown
                                :options="gatewaysOptions"
                                search
                                selection
                                :placeholder="$t('common.transactionSoftware')"
                                v-model="currentSelectedGatewayToCopy"
                        />
                    </sui-form-field>
                    <sui-button>{{ $t('common.copy') }}</sui-button>
                    <sui-button @click="saveBindings" :content="$t('common.save')" color="violet"/>
                </sui-form-fields>
            </sui-form>
        </div>

        <div class="large-content">
            <sui-grid :columns="2">
                <sui-grid-row>
                    <sui-grid-column :width="9">

                        <sui-segment :loading="isLoading">
                            <sui-table color="violet" celled striped v-for="(value, name) in bindings" :key="name">
                                <sui-table-header>
                                    <sui-table-row>
                                        <sui-table-headerCell class="cursorPointer" colspan="3"
                                                              v-on:click="collapse(name)">
                                            <sui-icon name="sort"/>
                                            <span v-if="isDisplayCode">{{ name }}</span>
                                            <span v-else>{{ $t(`${name}._title`) }}</span>
                                        </sui-table-headerCell>
                                    </sui-table-row>
                                    <sui-table-row :class="name">
                                        <sui-table-header-cell :width="4">{{
                                                $t('bindings.form.dataToMap')
                                            }}
                                        </sui-table-header-cell>
                                        <sui-table-header-cell :width="4">{{
                                                $t('bindings.form.relatedField')
                                            }}
                                        </sui-table-header-cell>
                                        <sui-table-header-cell :width="8">{{
                                                $t('bindings.form.examples')
                                            }}
                                        </sui-table-header-cell>
                                    </sui-table-row>
                                </sui-table-header>

                                <sui-table-body :class="name">
                                    <sui-table-row v-for="binding of value" :key="binding.static_binding.id">
                                        <sui-table-cell :title="binding.static_binding.code">
                                            <div v-if="!binding.field">
                                                <sui-label color="red" size="tiny" style="width: 100%">
                                                    <sui-icon name="exclamation triangle"
                                                              style="margin-right: 5px !important"></sui-icon>
                                                    {{ $t('bindings.form.noMap') }}
                                                </sui-label>
                                            </div>
                                            <div v-else-if="listing && listing.content && doesFieldExist(binding.field) === false">
                                                <sui-label color="orange" size="tiny" style="width: 100%">
                                                    <sui-icon name="exclamation triangle"
                                                              style="margin-right: 5px !important"></sui-icon>
                                                    &nbsp;
                                                    {{ $t('bindings.form.fieldsNotExists') }}
                                                </sui-label>
                                            </div>
                                            <span v-if="isDisplayCode">{{ binding.static_binding.code }}</span>
                                            <span v-else>{{ binding.label }}</span>
                                        </sui-table-cell>
                                        <sui-table-cell>
                                            <sui-input
                                                    size="mini"
                                                    fluid
                                                    v-model="binding.field"
                                            />
                                        </sui-table-cell>
                                        <sui-table-cell style="word-break: break-all">
                                            {{ getListingExampleValue(binding.field) }}
                                        </sui-table-cell>
                                    </sui-table-row>
                                </sui-table-body>
                            </sui-table>
                        </sui-segment>

                    </sui-grid-column>
                    <sui-grid-column :width="7">

                        <sui-segment :loading="isLoadingSelection" class="sticky-segment">

                            <sui-form>
                                <div class="segment-header">
                                    <h2 is="sui-header">{{ $t('bindings.mandateExample') }}</h2>
                                    <sui-button
                                            v-if="currentSelectedListing"
                                            size="tiny"
                                            :content="$t('bindings.form.outputMandate')"
                                            icon="external alternate"
                                            label-position="right"
                                            type="button"
                                            @click="openListingFile"
                                    />
                                </div>

                                <sui-form-fields fields="three">
                                    <sui-form-field>
                                        <label>{{ $t('common.name') }}</label>
                                        <sui-dropdown
                                                :options="customersOptions"
                                                search
                                                selection
                                                v-model="currentSelectedCustomer"
                                                @input="setGatewayFilesOptions"
                                                @keyup="handleSearchCustomer"
                                                ref="customers"
                                        />
                                    </sui-form-field>
                                    <sui-form-field>
                                        <label>{{ $t('common.gateway') }}</label>
                                        <sui-dropdown
                                                :options="gatewayFilesOptions"
                                                search
                                                selection
                                                v-model="currentSelectedGatewayFile"
                                                @input="setListingsOptions"
                                        />
                                    </sui-form-field>
                                    <sui-form-field>
                                        <label>{{ $t('common.mandate') }}</label>
                                        <sui-dropdown
                                                :options="listingsOptions"
                                                search
                                                selection
                                                v-model="currentSelectedListing"
                                                @input="loadListing"
                                        />
                                    </sui-form-field>
                                </sui-form-fields>
                            </sui-form>

                            <div v-if="this.listingPreview">
                                <MonacoEditor
                                        width="100%"
                                        height="80vh"
                                        theme="vs-dark"
                                        v-model="this.listingPreview"
                                        language="json"
                                ></MonacoEditor>
                            </div>

                        </sui-segment>

                    </sui-grid-column>
                </sui-grid-row>
            </sui-grid>
        </div>

    </div>
</template>

<script>
import store from '@/store';
import {api} from '@/api';
import jsonpath from 'jsonpath';
import MonacoEditor from 'monaco-editor-vue';
import {debounce} from '@/utils';
import {regroupBindings} from "@/helper/regroupBindings";

export default {
    name: 'GatewayBindings',
    components: {
        MonacoEditor,
    },
    beforeDestroy() {
        store.commit('removeAppTitle');
    },
    mounted() {
        store.commit('setAppTitle', `${this.$t('common.binding')}`);
    },
    data() {
        return {
            isModalOpen: false,
            state: store.state,
            isLoading: true,
            isLoadingSelection: true,
            gatewayId: this.$route.params.gateway_id,
            gateway: {
                id: this.$route.params.gateway_id,
                name: null,
            },
            listing: {
                id: null,
                content: null,
                content_link: null,
            },
            listingPreview: null,
            bindings: [],
            customers: [],
            gatewaysOptions: [],
            customersOptions: [],
            gatewayFilesOptions: [],
            listingsOptions: [],
            currentSelectedCustomer: null,
            currentSelectedGatewayFile: null,
            currentSelectedListing: null,
            currentSelectedGatewayToCopy: null,
            customerNameFilter: null,
            debouncedFetchCustomer: null,
            isDisplayCode: false,
        }
    },
    async created() {
        this.isLoading = true;
        await this.fetchGatewaysList();
        await this.fetchGatewayBindings(false);
        this.isLoading = false;

        store.commit('setAppTitle', `${this.$t('common.binding')} - ${this.gateway.name}`);

        this.isLoadingSelection = true;
        await this.fetchCustomers();
        this.isLoadingSelection = false;
        this.debouncedFetchCustomer = debounce(this.fetchCustomers, 500);
    },
    methods: {
        openListingFile() {
            if (this.listing) {
                window.open(this.listing.content_link, '_blank');
            }
        },
        toggleModal() {
            this.isModalOpen = !this.isModalOpen;
        },
        async saveBindings() {
            this.isLoading = true;

            let body = [];
            for (const [, value] of Object.entries(this.bindings)) {
                body = [
                    ...body,
                    ...value
                        .filter(binding => !!binding.field)
                        .map(binding => ({
                            static_binding_id: binding.static_binding.id,
                            field: binding.field,
                        }))
                ]
            }

            try {
                await api.bindings.save(this.gatewayId, body);

                this.$toastr.s(this.$t('bindings.saveSuccess'));
            } catch (err) {
                this.$toastr.e(this.$t('bindings.saveError'));
            } finally {
                this.isLoading = false;
            }
        },
        async fetchGatewaysList() {
            this.gateways = await api.bindings.getGateways(this.gatewayId);
            this.setGatewaysOptions();
        },
        async fetchGatewayBindings(gatewayId) {
            const isCopy = !!gatewayId;
            try {
                const {bindings, gateway} = await api.bindings.get(gatewayId || this.gatewayId);
                if (isCopy === false) this.gateway = gateway;
                this.bindings = regroupBindings(bindings);
                if (isCopy) {
                    this.$toastr.s(this.$t('bindings.copySuccess'));
                }
            } catch (err) {
                console.error(err);
                if (isCopy) {
                    this.$toastr.e(this.$t('bindings.copyError'));
                }
            }
        },
        async fetchCustomers() {
            this.customers = await api.bindings.getCustomers(this.gatewayId, this.customerNameFilter);
            this.setCustomersOptions();
        },
        async copyBindings(e) {
            e.preventDefault();

            if (!this.currentSelectedGatewayToCopy) {
                return;
            }

            await this.fetchGatewayBindings(this.currentSelectedGatewayToCopy);
        },
        async loadListing() {
            this.listing = await api.bindings.getListing(this.gatewayId, this.currentSelectedListing);
            this.listingPreview = JSON.stringify(this.listing.content, null, 4)
        },
        resetSelected() {
            this.currentSelectedGatewayFile = null;
            this.currentSelectedListing = null;
            this.listing = null;
            this.listingPreview = null;
        },
        doesFieldExist(field) {
            return /^[a-zA-Z0-9_.]+$/.test(field) && jsonpath.paths(this.listing.content, `$..${field}`).length > 0;
        },
        getListingExampleValue(field) {
            if (!this.listing || !this.listing.content || !field || !/^[a-zA-Z0-9_.]+$/.test(field)) {
                return null;
            }

            const value = jsonpath.query(this.listing.content, `$..${field}`);

            if (!value) {
                return null;
            }

            return value[0];
        },
        getCurrentSelectedCustomer() {
            return this
                .customers
                .find(customer => customer.id === this.currentSelectedCustomer)
        },
        getCurrentSelectedGatewayFile() {
            if (!this.getCurrentSelectedCustomer()) {
                return null;
            }

            return this
                .getCurrentSelectedCustomer()
                .gateway_files
                .find(gf => gf.id === this.currentSelectedGatewayFile);
        },
        setCustomersOptions() {
            this.resetSelected();

            this.customersOptions = this.customers.map(customer => ({
                key: customer.id,
                text: customer.name,
                value: customer.id,
            }));

            if (this.customersOptions.length) {
                this.currentSelectedCustomer = this.customersOptions[0].key;
                this.setGatewayFilesOptions();
            }
        },
        async handleSearchCustomer() {
            if (this.customerNameFilter === this.$refs.customers.filter) {
                return;
            }

            this.customerNameFilter = this.$refs.customers.filter;
            this.debouncedFetchCustomer();
        },
        setGatewayFilesOptions() {
            this.resetSelected();
            this.customerNameFilter = null;

            this.gatewayFilesOptions = this.getCurrentSelectedCustomer()
                .gateway_files
                .map(gf => ({
                    key: gf.id,
                    text: gf.name,
                    value: gf.id,
                }));

            if (this.gatewayFilesOptions.length) {
                this.currentSelectedGatewayFile = this.gatewayFilesOptions[0].key;
                this.setListingsOptions();
            }
        },
        setListingsOptions() {
            this.listingsOptions = this.getCurrentSelectedGatewayFile()
                .listings
                .map(listing => ({
                    key: listing.id,
                    text: listing.reference,
                    value: listing.id,
                }));

            if (this.listingsOptions.length) {
                this.currentSelectedListing = this.listingsOptions[0].key;
                this.loadListing();
            }
        },
        setGatewaysOptions() {
            this.gatewaysOptions = this.gateways
                .map(gateway => ({
                    key: gateway.id,
                    text: gateway.name,
                    value: gateway.id,
                }));
        },
        collapse(section) {
            document.getElementsByClassName(section).forEach((element) => element.classList.toggle('hidden'));
        },
        displayCode() {
            this.isDisplayCode = !this.isDisplayCode;
        }
    },
}
</script>

<style>
.segment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.form-header {
    text-align: right;
}

.form-header .fields {
    display: inline-flex !important;
}

.hidden {
    display: none;
}

th.cursorPointer {
    cursor: pointer !important;
}

.sticky-segment {
    top: 20px;
    position: sticky !important;
}

</style>
